import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { SymbolIcon, SYMBOL_KEYS } from "./SymbolIcon";
import api from "../lib/api";

const CATEGORIES = ["domestic", "sexual", "harassment", "psychological", "other"];
const MAX = 500;

async function reverseCountry(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=3`,
      { headers: { "Accept-Language": "en" } }
    );
    const data = await res.json();
    return (data?.address?.country_code || "").toUpperCase() || null;
  } catch {
    return null;
  }
}

export function StoryPanel({ point, onClose, onSubmitted }) {
  const { t } = useLang();
  const [symbol, setSymbol] = useState("candle");
  const [category, setCategory] = useState("domestic");
  const [text, setText] = useState("");
  const [nickname, setNickname] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const open = !!point;

  const submit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      const country = await reverseCountry(point.lat, point.lng);
      await api.post("/stories", {
        symbol,
        category,
        text: text.trim(),
        nickname: nickname.trim() || null,
        lat: point.lat,
        lng: point.lng,
        country,
      });
      toast.success(t("submitted_toast"));
      setText("");
      setNickname("");
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(t("error_toast"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          data-testid="story-panel"
          initial={{ x: 420, opacity: 0.6 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 420, opacity: 0 }}
          transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="absolute right-0 top-0 z-[1000] flex h-full w-full max-w-[400px] flex-col bg-white shadow-softlg"
        >
          <div className="flex items-start justify-between p-6 pb-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-graphite">{t("panel_title")}</h2>
              <p className="mt-1 text-sm leading-relaxed text-textsec">{t("panel_sub")}</p>
            </div>
            <button
              data-testid="panel-close-btn"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-softpink text-textsec transition-colors hover:text-graphite"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {/* symbols */}
            <label className="text-sm font-medium text-graphite">{t("choose_symbol")}</label>
            <div className="mt-3 grid grid-cols-7 gap-2">
              {SYMBOL_KEYS.map((s) => (
                <button
                  key={s}
                  data-testid={`symbol-${s}`}
                  onClick={() => setSymbol(s)}
                  title={t(`sym_${s}`)}
                  className={`flex aspect-square items-center justify-center rounded-xl border transition-all ${
                    symbol === s
                      ? "border-dusty bg-softpink text-dusty"
                      : "border-transparent bg-softpink/50 text-textsec hover:text-dusty"
                  }`}
                >
                  <SymbolIcon name={s} size={20} />
                </button>
              ))}
            </div>

            {/* categories */}
            <label className="mt-6 block text-sm font-medium text-graphite">{t("choose_category")}</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`category-${c}`}
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    category === c ? "bg-dusty text-white" : "bg-softpink text-textsec hover:text-graphite"
                  }`}
                >
                  {t(`cat_${c}`)}
                </button>
              ))}
            </div>

            {/* text */}
            <label className="mt-6 block text-sm font-medium text-graphite">{t("your_words")}</label>
            <textarea
              data-testid="story-textarea"
              value={text}
              maxLength={MAX}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("placeholder_text")}
              rows={5}
              className="mt-3 w-full resize-none rounded-panel bg-softpink px-4 py-3 text-sm leading-relaxed text-graphite outline-none ring-dusty/40 placeholder:text-textsec/70 focus:ring-2"
            />
            <div className="mt-1 text-right text-xs text-textsec" data-testid="char-count">
              {text.length}/{MAX}
            </div>

            {/* nickname */}
            <label className="mt-4 block text-sm font-medium text-graphite">{t("nickname")}</label>
            <input
              data-testid="nickname-input"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t("nickname_ph")}
              maxLength={40}
              className="mt-3 w-full rounded-btn bg-softpink px-4 py-2.5 text-sm text-graphite outline-none ring-dusty/40 placeholder:text-textsec/70 focus:ring-2"
            />

            <p className="mt-5 rounded-xl bg-softpink px-4 py-3 text-xs leading-relaxed text-textsec">
              {t("disclaimer")}
            </p>
          </div>

          {/* actions */}
          <div className="flex gap-3 border-t border-softpink p-6">
            <button
              data-testid="panel-cancel-btn"
              onClick={onClose}
              className="flex-1 rounded-btn bg-softpink px-5 py-3 text-sm font-medium text-graphite transition-colors hover:bg-softpink/70"
            >
              {t("cancel")}
            </button>
            <button
              data-testid="panel-submit-btn"
              onClick={submit}
              disabled={!text.trim() || submitting}
              className="flex-1 rounded-btn bg-dusty px-5 py-3 text-sm font-medium text-white shadow-soft transition-colors hover:bg-dusty-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? t("submitting") : t("submit")}
            </button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
